import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import FlexBox from 'common/components/FlexBox';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useSelector } from 'react-redux';
import Stripe from 'stripe';
import { LoadingData } from 'common/types';
import { isDevEnv } from 'common/utils/common';
import { StripePaymentMethods } from './components/StripePaymentMethods';
import { PaymentBlockSkeleton } from '../../components/PaymentBlockSkeleton';
import { StripeStatus } from './components/StripeStatus';
import { SetUpButton } from './components/SetupButton';
import { ActionRequiredBanner } from './components/ActionRequiredBanner';
import { useTranslation } from 'services/localization';
import { Callable } from 'common/frontend/callable';
import { PaymentProviders } from 'common/modules/payments/constants';
import { PaymentMethodIntro } from '../../components/PaymentMethodIntro';

type StripeProps = {
	styles: {
		titleVariant: 'h5' | 'h6';
		buttonVariant: 'outlined' | 'contained';
	};
	rentlePaymentsSupported: boolean;
};

const StripeSection = (props: StripeProps) => {
	const { styles, rentlePaymentsSupported } = props;
	const { titleVariant, buttonVariant } = styles;
	const { t } = useTranslation();
	const activeShopId = useSelector(ShopSelectors.activeShopId)!;
	const shopReadOnly = useSelector(ShopSelectors.activeShopReadOnly);
	const stripeAccountId = shopReadOnly?.integrations?.STRIPE?.accountId;
	const [stripeAccount, setStripeAccount] = useState<LoadingData<Stripe.Account | null>>({
		loading: stripeAccountId ? true : false,
		data: null,
		error: null,
	});

	// Allow charges always in dev env so we can try out Stripe even with incomplete accounts
	const chargesEnabled = !!stripeAccount.data?.charges_enabled || isDevEnv();

	useEffect(() => {
		const fetchStripeAccount = async (accountId: string) => {
			const _stripeAccount = await Callable.paymentAccounts.stripe.get({
				accountId,
				shopId: activeShopId,
			});
			setStripeAccount({ loading: false, data: _stripeAccount, error: null });
		};
		if (stripeAccountId) {
			setStripeAccount({ loading: true, data: null, error: null });
			fetchStripeAccount(stripeAccountId);
		}
	}, [stripeAccountId, activeShopId]);

	return (
		<>
			<Header>
				<StripeTitle titleVariant={titleVariant} />
				{!stripeAccountId ? (
					<SetUpButton
						accountId={null}
						variant={buttonVariant}
						color="primary"
						text={`${t('common:actions.connect', 'Connect')} Stripe`}
					/>
				) : !stripeAccount.data ? null : (
					<StripeStatus account={stripeAccount.data} />
				)}
			</Header>
			<Content>
				{!!stripeAccount.data && <ActionRequiredBanner account={stripeAccount.data} />}
				<Box mt={3} />
				{stripeAccount.loading ? (
					<PaymentBlockSkeleton rows={1} />
				) : !stripeAccount.data && !rentlePaymentsSupported ? (
					<PaymentMethodIntro
						paymentProvider={PaymentProviders.stripe}
						paymentMethods={`Visa, MasterCard`}
					/>
				) : !stripeAccount.data ? null : (
					<StripePaymentMethods chargesEnabled={chargesEnabled} />
				)}
			</Content>
		</>
	);
};

const Header: React.FC<{}> = ({ children }) => (
	<FlexBox justifyContent="space-between">{children}</FlexBox>
);

const Content: React.FC<{}> = ({ children }) => <Box mt={3}>{children}</Box>;

type StripeTitleProps = {
	titleVariant: 'h5' | 'h6';
};

const StripeTitle = (props: StripeTitleProps) => {
	const { titleVariant } = props;
	const { t } = useTranslation();
	return (
		<Box>
			<Typography variant={titleVariant}>{'Stripe'}</Typography>
			<Typography variant="body2" color="textSecondary">
				{t('account.payments.stripeDescription', 'Card payments offered by Stripe')}
			</Typography>
		</Box>
	);
};

export default StripeSection;
